import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Network, CalendarClock, FileSearch } from 'lucide-react';
import { useInView } from '@/hooks/useInView';
import { useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CALENDLY_URL = 'https://calendly.com/hello-kingmakersinc/discovery';

function RevealSection({ children, className = '', delay = 0 }) {
  const [ref, isInView] = useInView();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

function GoldButton({ href, to, children, ...props }) {
  const className =
    'inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer';
  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className} {...props}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className} {...props}>
      {children}
    </Link>
  );
}

function Hero() {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative px-6 lg:px-8 pt-[120px] pb-[100px] md:pt-[220px] md:pb-[200px] overflow-hidden"
    >
      {/* Gold hero glow per DESIGN-SYSTEM.md §10 blur system, §12.1 gold accent */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2 h-[500px] w-[500px] rounded-full bg-km-gold/10 blur-[100px]"
      />
      <div className="relative max-w-4xl mx-auto text-center">
        <RevealSection>
          <span className="inline-flex items-center px-4 py-1.5 rounded-full border border-km-gold/30 bg-km-gold/10 font-inter text-xs font-semibold tracking-[0.2em] uppercase text-km-gold">
            AI Systems and Consulting
          </span>
        </RevealSection>
        <RevealSection delay={100}>
          <h1 className="mt-8 font-inter font-bold text-[38px] md:text-[54px] lg:text-[68px] leading-[1.1] tracking-tight text-km-white">
            We build AI agents that do real work.
          </h1>
        </RevealSection>
        <RevealSection delay={200}>
          <p className="mt-8 font-inter font-medium text-lg md:text-xl text-km-gray-5 leading-[1.3] max-w-2xl mx-auto">
            King Makers builds production AI systems, from workforce scheduling to business diagnostics, and provides the consulting to deploy them in your business.
          </p>
        </RevealSection>
        <RevealSection delay={300}>
          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <GoldButton href={CALENDLY_URL} data-testid="hero-primary-cta">
              Book a Discovery Call
            </GoldButton>
            <Link
              to="/products"
              data-testid="hero-see-all"
              className="inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-[10px] border-2 border-km-gray-4/50 font-inter font-medium text-sm tracking-wide text-km-white hover:border-km-gold/30 transition-colors duration-300 cursor-pointer"
            >
              See Our Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </RevealSection>
      </div>
    </section>
  );
}

function ProductsStrip() {
  const cards = [
    {
      slug: 'scheduling-agent',
      to: '/products',
      // flagship: ring plus glow per DESIGN-SYSTEM.md §12.9
      ringClass: 'border-2 border-km-gold/30 shadow-km-gold-soft hover:border-km-gold/50',
      badge: 'Live in Production',
      badgeClass: 'border-km-gold/30 bg-km-gold/10 text-km-gold',
      title: 'King Makers Scheduling Agent',
      copy: 'Multi-tenant AI workforce scheduling. Claude-powered shift assignment with hard constraint enforcement, confidence scoring, and human approval before anything publishes.',
    },
    {
      slug: 'consulting-autopilot',
      to: '/products',
      ringClass: 'border-2 border-km-cyan-2/30 hover:border-km-cyan-2/50',
      badge: 'Agent Platform',
      badgeClass: 'border-km-cyan-2/30 bg-km-cyan-2/10 text-km-cyan-1',
      title: 'Consulting Autopilot',
      copy: 'A multi-agent diagnostic platform. Researcher, Analyst, and Writer agents produce consulting-grade business diagnostics from a single intake.',
    },
    {
      slug: 'high-stakes-narrative-pack',
      to: '/products/high-stakes-narrative-pack',
      ringClass: 'border-2 border-km-gold/30 hover:border-km-gold/50',
      badge: 'Toolkit',
      badgeClass: 'border-km-gray-4/50 bg-km-gray-4/20 text-km-gray-5',
      title: 'High-Stakes Narrative Pack',
      copy: 'A structured Notion system for high-stakes communication: frame, build, de-risk, deploy, land.',
    },
  ];

  return (
    <section data-testid="products-strip-section" className="px-6 lg:px-8 py-[80px] md:py-[120px]">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">What We Build</p>
          <h2 className="font-inter font-bold text-[30px] md:text-[44px] lg:text-[56px] leading-[1.1] tracking-tight text-km-white">
            Systems in production. Not demos.
          </h2>
        </RevealSection>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-[30px]">
          {cards.map((card, i) => (
            <RevealSection key={card.slug} delay={i * 120}>
              <Link
                to={card.to}
                data-testid={`product-card-${card.slug}`}
                className={`block h-full p-[30px] rounded-[10px] bg-km-card-gradient transition-colors duration-300 cursor-pointer ${card.ringClass}`}
              >
                <span className={`inline-flex items-center px-3 py-1 rounded-full border font-inter text-[11px] font-semibold tracking-[0.15em] uppercase ${card.badgeClass}`}>
                  {card.badge}
                </span>
                <h3 className="mt-6 font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">
                  {card.title}
                </h3>
                <p className="mt-4 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">
                  {card.copy}
                </p>
              </Link>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorks() {
  const steps = [
    { num: '1', title: 'Discovery call', desc: '30 minutes on your operations, constraints, and where decisions bottleneck.' },
    { num: '2', title: 'Diagnostic', desc: 'Our agent systems analyze your business and produce a consulting-grade assessment.' },
    { num: '3', title: 'Deploy', desc: 'We build or configure the AI system that removes the bottleneck, and stay until it runs.' },
  ];

  return (
    <section data-testid="how-it-works-section" className="px-6 lg:px-8 py-[80px] md:py-[120px] bg-km-dark-vertical">
      <div className="max-w-5xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4 text-center">How It Works</p>
          <h2 className="font-inter font-bold text-[26px] md:text-[38px] lg:text-[46px] leading-[1.1] tracking-tight text-km-white text-center">
            Three steps from bottleneck to running system.
          </h2>
        </RevealSection>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-[40px]">
          {steps.map((step, i) => (
            <RevealSection key={step.num} delay={i * 120}>
              <div className="text-center md:text-left">
                <span className="inline-flex items-center justify-center w-12 h-12 rounded-[16px] bg-km-gold-gradient font-inter font-bold text-xl text-km-gray-1">
                  {step.num}
                </span>
                <h3 className="mt-5 font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">{step.title}</h3>
                <p className="mt-3 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">{step.desc}</p>
              </div>
            </RevealSection>
          ))}
        </div>
      </div>
    </section>
  );
}

function Capabilities() {
  const items = [
    {
      icon: Cpu,
      title: 'Production AI systems',
      desc: 'Systems that run real operations, with human approval before anything publishes.',
    },
    {
      icon: Network,
      title: 'Multi-agent architectures',
      desc: 'Researcher, Analyst, and Writer agents working together on a single intake.',
    },
    {
      icon: CalendarClock,
      title: 'Constraint-based scheduling intelligence',
      desc: 'Claude-powered shift assignment with hard constraint enforcement and confidence scoring.',
    },
    {
      icon: FileSearch,
      title: 'AI-accelerated business diagnostics',
      desc: 'Consulting-grade assessments of your business, produced by agent systems.',
    },
  ];

  return (
    <section data-testid="capabilities-section" className="px-6 lg:px-8 py-[80px] md:py-[120px]">
      <div className="max-w-7xl mx-auto">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Capabilities</p>
          <h2 className="font-inter font-bold text-[30px] md:text-[44px] lg:text-[56px] leading-[1.1] tracking-tight text-km-white">
            What we bring to the table.
          </h2>
        </RevealSection>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 gap-[30px]">
          {items.map((item, i) => {
            const Icon = item.icon;
            return (
              <RevealSection key={item.title} delay={i * 80}>
                <div className="h-full p-[30px] rounded-[10px] border border-km-gray-3 bg-km-card-gradient">
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-[16px] bg-km-icon-gradient shadow-km-gray-glow">
                    <Icon className="w-6 h-6 text-km-gold" />
                  </div>
                  <h3 className="mt-5 font-inter font-medium text-[20px] md:text-[24px] leading-[1.2] text-km-white">{item.title}</h3>
                  <p className="mt-3 font-inter font-medium text-sm text-km-gray-5 leading-[1.3]">{item.desc}</p>
                </div>
              </RevealSection>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ClosingCta() {
  return (
    <section data-testid="closing-cta-section" className="px-6 lg:px-8 py-[80px] md:py-[120px]">
      <div className="max-w-5xl mx-auto">
        <RevealSection>
          <div className="relative overflow-hidden text-center p-10 md:p-16 rounded-[16px] bg-km-card-gradient border-2 border-km-gold/30 shadow-km-gold-soft">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute left-1/2 -top-[250px] -translate-x-1/2 h-[500px] w-[500px] rounded-full bg-km-gold/10 blur-[100px]"
            />
            <h2 className="relative font-inter font-bold text-[26px] md:text-[38px] lg:text-[46px] leading-[1.1] tracking-tight text-km-white max-w-3xl mx-auto">
              Ready to see what an AI agent could take off your plate?
            </h2>
            <div className="relative mt-10 flex flex-col items-center gap-5">
              <GoldButton href={CALENDLY_URL} data-testid="closing-cta">
                Book a Discovery Call
              </GoldButton>
              <Link
                to="/contact"
                data-testid="closing-cta-contact"
                className="font-inter font-medium text-sm text-km-gray-5 hover:text-km-gold transition-colors cursor-pointer"
              >
                Or write to us
              </Link>
            </div>
          </div>
        </RevealSection>
      </div>
    </section>
  );
}

function Newsletter() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter`, { email });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  };

  return (
    <section data-testid="newsletter-section" className="px-6 lg:px-8 pb-[100px]">
      <div className="max-w-2xl mx-auto text-center">
        <RevealSection>
          <p className="font-inter text-xs font-semibold tracking-[0.25em] uppercase text-km-gold mb-4">Stay Sharp</p>
          <h2 className="font-inter font-bold text-[24px] md:text-[28px] leading-[1.1] tracking-tight text-km-white">
            Get notified when new systems launch.
          </h2>
          <p className="mt-4 font-inter font-medium text-km-gray-5 leading-[1.3]">
            Plus occasional dispatches on decision-making, leadership clarity, and founder leverage. No fluff.
          </p>
          <form onSubmit={handleSubmit} data-testid="newsletter-form" className="mt-8 flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              data-testid="newsletter-email-input"
              className="flex-1 h-12 px-4 rounded-[10px] border border-km-gray-3 bg-km-gray-2 font-inter text-sm text-km-white placeholder:text-km-gray-5 focus:outline-none focus:ring-1 focus:ring-km-gold transition-all"
              required
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={status === 'loading'}
              className="inline-flex items-center justify-center h-12 px-6 rounded-[10px] bg-km-gold text-km-gray-1 font-inter font-medium text-sm tracking-wide hover:bg-km-gold-deep transition-colors duration-300 cursor-pointer disabled:opacity-50"
            >
              {status === 'loading' ? 'Subscribing...' : 'Subscribe'}
            </button>
          </form>
          {status === 'success' && (
            <p data-testid="newsletter-success" className="mt-3 font-inter text-sm text-km-gold">Subscribed. You'll hear from us.</p>
          )}
          {status === 'error' && (
            <p data-testid="newsletter-error" className="mt-3 font-inter text-sm text-red-400">Something went wrong. Try again.</p>
          )}
        </RevealSection>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <ProductsStrip />
      <HowItWorks />
      <Capabilities />
      <ClosingCta />
      <Newsletter />
    </div>
  );
}
